import React from 'react';
import { CheckIcon, LockIcon } from 'lucide-react';
import { Reveal } from './Reveal';
import { ConfettiRain } from './ConfettiRain';
import { CTAButton } from './CTAButton';
import { DiscountBadge } from './PriceBlock';
import { MarketSelector } from './MarketSelector';
import { useMarket } from '../contexts/MarketContext';
import { images, PACK_VALUE_USD } from '../data/bonuses';
import { estimatedAmount, formatLocal } from '../utils/money';
export function Offer() {
  const {
    market
  } = useMarket();

  /* Estimated pack value and savings, always in the visitor's own currency. */
  const packValue = estimatedAmount(PACK_VALUE_USD, market);
  const packSavings = Math.max(packValue - market.priceAmount, 0);
  const stackTotal = formatLocal(packValue, market);
  const stackSavings = formatLocal(packSavings, market);
  return <section id="oferta" className="relative w-full overflow-hidden bg-cream py-16 sm:py-24" aria-labelledby="offer-title" data-id="element-211" data-mp="2fav4ark2i0sg">
      <ConfettiRain count={14} opacity={0.5} data-id="element-212" data-mp="pk7eriduof7v" />

      <div className="relative z-10 mx-auto max-w-content px-5 sm:px-8" data-id="element-213" data-mp="pjfkqbzwipjp">
        <Reveal data-id="element-214" data-mp="uisdtehd8suq">
          <h2 id="offer-title" className="text-center font-display text-2xl font-extrabold uppercase leading-tight text-navy sm:text-4xl" data-id="element-215" data-mp="ui8v3vjw60fn">
            Todo esto es <span className="text-brandgreen" data-id="element-216" data-mp="13k9vh1af92lj">tuyo hoy</span>
          </h2>
        </Reveal>

        <Reveal delay={0.1} data-id="element-217" data-mp="6uio41h17kyb">
          <div className="mt-10 grid grid-cols-1 overflow-hidden rounded-[2rem] border border-navy/10 bg-white shadow-card lg:grid-cols-[0.95fr_1.05fr]" data-id="element-218" data-mp="6val9gqa1qqp">
            <div className="bg-cream2 p-6 sm:p-9" data-id="element-219" data-mp="funv4aibchby">
              <img src={images.bundle} alt="El Gran Reto Creativo con los 10 bonos gratis incluidos" className="mx-auto h-auto w-full max-w-[420px] rounded-3xl shadow-soft" width={1456} height={1092} loading="lazy" decoding="async" data-id="element-220" data-mp="ft4f8l0kk8ap" />
              <ul className="mx-auto mt-6 flex max-w-xs flex-col gap-2 sm:max-w-none" data-id="element-221" data-mp="vez0op1lhb0v">
                {['El Gran Reto Creativo · 365 actividades', '10 bonos GRATIS', 'Pasaporte Creativo'].map(i => <li key={i} className="flex items-center gap-2 font-sans text-sm text-ink" data-id="element-222" data-mp="106pfb77keky3">
                    <CheckIcon className="h-4 w-4 shrink-0 text-brandgreen" aria-hidden="true" data-id="element-223" data-mp="cgv5yzmgfeil" />
                    {i}
                  </li>)}
              </ul>
            </div>

            <div className="p-6 text-center sm:p-9 lg:text-left" data-id="element-224" data-mp="lhkxvs8lgkr1">
              <div className="flex flex-wrap items-center justify-center gap-3 lg:justify-between" data-id="element-225" data-mp="livyxkanneeg">
                <span className="font-display text-[11px] font-extrabold uppercase tracking-[0.18em] text-muted" data-id="element-226" data-mp="heo9tm0abwfp">
                  Precio en {market.country}
                </span>
                <MarketSelector tone="light" id="market-selector-offer" data-id="element-227" data-mp="1sucw0b5psrfg" />
              </div>

              <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-3 lg:justify-start" data-id="element-228" data-mp="20duju0pzs3ht">
                <span className="font-sans text-base text-muted line-through decoration-offer decoration-[2.5px]" data-id="element-229" data-mp="25fbhlnd6j9u6">
                  {market.compareAt}
                </span>
                <span className="font-display text-5xl font-extrabold leading-none text-navy sm:text-6xl" data-id="element-230" data-mp="1vglzbthdfnde">
                  {market.price}
                </span>
                <DiscountBadge size="lg" data-id="element-231" data-mp="2o48b3p0r5ybc" />
              </div>
              <p className="mt-3 font-sans text-sm font-semibold text-offer" data-id="element-232" data-mp="2o3gh2imt08n6">
                Ahorras {market.savings}
              </p>

              <div className="mt-7" data-id="element-233" data-mp="2eeez4mwp7h6k">
                <CTAButton fullWidth data-id="element-234" data-mp="2eddhfzl6fwfv">Quiero todo por {market.price}</CTAButton>
              </div>

              <ul className="mx-auto mt-5 grid max-w-xs grid-cols-1 gap-2 sm:max-w-none sm:grid-cols-3" data-id="element-235" data-mp="322y7t3jntvz6">
                {['Pago único', 'Acceso digital inmediato', 'Garantía 7 días'].map(i => <li key={i} className="flex items-center gap-1.5 font-sans text-xs text-muted" data-id="element-236" data-mp="1j7fv56z8gbne">
                    <CheckIcon className="h-3.5 w-3.5 shrink-0 text-brandgreen" aria-hidden="true" data-id="element-237" data-mp="1988cq5dn5qtf" />
                    {i}
                  </li>)}
              </ul>

              {/* Estimated value vs. what you actually pay */}
              <div className="mt-6 rounded-2xl bg-cream p-5 text-left" data-id="element-238" data-mp="1wx9h9hjcshtf">
                <div className="flex items-baseline justify-between gap-4" data-id="element-239" data-mp="1x05gd96xtcjg">
                  <span className="font-display text-xs font-extrabold uppercase text-muted" data-id="element-240" data-mp="1s59ny7xsgn5t">
                    Valor estimado total
                  </span>
                  <span className="shrink-0 font-display text-sm font-extrabold text-muted line-through decoration-offer decoration-2" data-id="element-241" data-mp="2knna3a9rikp2">
                    {stackTotal}
                  </span>
                </div>

                <div className="mt-2 flex items-baseline justify-between gap-4" data-id="element-242" data-mp="2o6mg7ylv272y">
                  <span className="font-display text-xs font-extrabold uppercase text-navy" data-id="element-243" data-mp="2s64q7zc58xr0">
                    Hoy pagas
                  </span>
                  <span className="shrink-0 font-display text-base font-extrabold text-navy" data-id="element-244" data-mp="2x82fba786mrv">
                    {market.price}
                  </span>
                </div>

                <div className="mt-3 flex items-baseline justify-between gap-4 rounded-xl bg-mint px-3 py-2" data-id="element-245" data-mp="373nnjq2w0ja2">
                  <span className="font-display text-xs font-extrabold uppercase text-brandgreen" data-id="element-246" data-mp="3b3xt51mrp7oe">
                    Te ahorras (valor estimado)
                  </span>
                  <span className="shrink-0 font-display text-base font-extrabold text-brandgreen" data-id="element-247" data-mp="3kuip0hb23y3m">
                    {stackSavings}
                  </span>
                </div>

                <p className="mt-3 font-sans text-[10px] leading-relaxed text-muted/70" data-id="element-248" data-mp="3ut84k3fhd6gp">
                  Valores estimados de referencia en {market.currency}.
                </p>
              </div>

              <p className="mt-5 flex items-center justify-center gap-1.5 font-sans text-[11px] text-muted lg:justify-start" data-id="element-249" data-mp="307aoxomjmxnn">
                <LockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-250" data-mp="3ew73m4j3v8x8" />
                Pago seguro
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>;
}